import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNotification } from "../../../../../hooks/useNotification.jsx";
import { useUpdateBulkReceiptLineStatusMutation } from "../../../../../store/apiSlice/receiptApiSlice.js";
import { setCurrentViewInLocals } from "../../../../../store/slice/newInterfaceSlice.js";
import { BillActionWithCheck } from "../../../../bill/layout/action.jsx";
import { ChartsAccountActionWithCheck } from "../../../../chartsOfAccount/actions/chartsOfAccountAction.jsx";
import { CoManufacturingOrderActionWithCheck } from "../../../../coManufacturingOrder/list/action/action.jsx";
import { CustomerSegmentActionWithCheck } from "../../../../customerSegment/actions/CustomerSegmentAction.jsx";
import { DeliveryActionWithCheck } from "../../../../delivery/layout/action/index.js";
import {
  CatalogueActionWithCheck,
  CatalogueRightAction,
  ShopifyLinkedProductsRightAction,
} from "../../../../integrationV1/shopify/catalogue/actions.jsx";
import { ManufacturingOrderActionWithCheck } from "../../../../manufacturingOrder/list/action/action.jsx";
import { TransferOrderActionWithCheck } from "../../../../order/transferV2/actions/TransferOrderAction.jsx";
import { PicklistActionWithCheck } from "../../../../picklist/layout/action/action.jsx";
import { PrepaymentActionWithCheck } from "../../../../prepayment/layout/action";
import { PriceListActionWithCheck } from "../../../../priceList/actions/PriceListAction.jsx";
import { ProductPlanActionWithCheck } from "../../../../productPlan/actions/action.jsx";
import { PromoActionWithCheck } from "../../../../promo/layout/action/action.jsx";
import { PurchaseOrderListActionsWithCheck } from "../../../../purchaseOrder/list/actions";
import { QuotationActionWithCheck } from "../../../../quotation/layout/action.jsx";
import { PlannedOrderActionWithCheck } from "../../../../planner/plannedOrder/layout/action/action.jsx";
import { applyIncludeConvertedParams } from "../../../../planner/plannedOrder/layout/eligibility.js";
import { ReplenishmentActionWithCheck } from "../../../../replenishment/layout/action/action.jsx";
import { SaleInvoiceActionWithCheck } from "../../../../saleInvoice/layout/action.jsx";
import { SalesOrderActionWithCheck } from "../../../../salesOrder/layout/action.jsx";
import { AsnActionWithCheck } from "../../../../shipment/asn/layout/action/index.jsx";
import { ShipmentLegActionWithCheck } from "../../../../shipmentLeg/layout/action/index.jsx";
import { ShipmentV1ActionWithCheck } from "../../../../shipmentV1/layout/action/index.jsx";
import { BomActionWithCheck } from "../../../../standardBom/list/action/action.jsx";
import { StockPackActionWithCheck } from "../../../../stockPack/layout/action/action.jsx";
import { TaxCategoryActionWithCheck } from "../../../../taxCategory/action/index.jsx";
import { BoardIcon } from "../../../assets/svgIcon/listView/Board";
import { Down } from "../../../assets/svgIcon/listView/Down";
import { Eye } from "../../../assets/svgIcon/listView/Eye";
import { ListIcon } from "../../../assets/svgIcon/listView/ListIcon";
import { ReactComponent as UpIcon } from "../../../assets/svgIcon/listView/UpBlue.svg";
import { ReactComponent as MenuIcon } from "../../../assets/svgIcon/shipment/Menu.svg";
import { Milestone } from "../../../components/Milestone/Milestone.jsx";
import { AllocationAction } from "../../Allocation/action/action.jsx";
import { allocationDataByType, SelectAllocation } from "../../Allocation/SelectAllocation.jsx";
import { InventoryDetailActionWithCheck } from "../../Inventory/actions/InventoryDetailAction.jsx";
import { ShipmentOrderActionWithCheck } from "../../Shipment/actions/shipmentOrderAction.jsx";
import { moduleForView } from "../../utils/tableDataElements.js";
import { SelectComponent } from "../SelectComponent/SelectComponent.jsx";
import { CommonAction } from "./Actions/commonAction.jsx";
import { CommonActionForMaster } from "./Actions/commonActionForMaster.jsx";
import { ListMenuOptions } from "./ListMenuOptions.jsx";
import { BoardSearch } from "./BoardSearch.jsx";
import { ShipmentTransportModeFilter } from "./ShipmentTransportModeFilter.jsx";
import { HideEmptyLanesMenu } from "./HideEmptyLanesMenu.jsx";
import { UsersListActions } from "../../../../user/layout/UsersListActions.jsx";

/**
 * OptionsForView Component
 *
 * Provides the action bar for list/board view of orders (shipment, inventory, etc.).
 * Includes actions for applying view rules, creating shipments, cargo-ready status,
 * filtering, exporting, and toggling between view types.
 *
 * @component
 * @param {Object} viewConfig - Contains current view state and setters.
 * @param {Object} orderConfig - Includes order type and allocation info.
 * @param {Object} selectionConfig - Contains selected row(s) and its setter.
 * @param {Object} callbacks - Helper functions to refresh list or modify state.
 */

function OptionsForView({ viewConfig, orderConfig, selectionConfig, callbacks }) {
  const {
    boardView,
    setBoardView,
    selectedCurrentView,
    setSelectedCurrentView,
    currentViewDetails,
    setIsOpenCreateView,
    hideEmptyLanesMode = "none",
    onHideEmptyLanesModeChange,
    transportMode = "all",
    onTransportModeChange,
  } = viewConfig;
  const { orderType, allocation, setAllocation, onInviteClick, hideViews } = orderConfig;
  const { selectedOrderFromListview, setSelectedOrder } = selectionConfig;
  const { getConsignmentByRule, setConsignmentsParamsLocal, listParams } = callbacks || {};

  const [isOpenMilestone, setIsOpenMilestone] = useState(false);
  const [isOpenAllocation, setIsOpenAllocation] = useState(false);
  const [isOpenListMenu, setIsOpenListMenu] = useState(false);

  const dispatch = useDispatch();
  const selectedOrderId = useMemo(
    () => [...new Set(selectedOrderFromListview?.map(item => item.id) || [])],
    [selectedOrderFromListview],
  );
  const openMileStoneView = () => {
    setIsOpenMilestone(true);
  };
  const closeMilestoneModal = () => {
    setIsOpenMilestone(false);
  };

  const openAllocation = () => {
    setIsOpenAllocation(true);
  };
  const closeAllocation = () => {
    setIsOpenAllocation(false);
  };

  const handleListView = () => {
    setBoardView(false);
    dispatch(
      setCurrentViewInLocals({
        view: currentViewDetails.listView,
        module: moduleForView[orderType],
      }),
    );
    setSelectedCurrentView(currentViewDetails.listView);
  };
  const handleBoardView = () => {
    setBoardView(true);
    dispatch(
      setCurrentViewInLocals({
        view: currentViewDetails.boardView,
        module: moduleForView[orderType],
      }),
    );
    setSelectedCurrentView(currentViewDetails.boardView);
  };

  const openListMenu = () => {
    setIsOpenListMenu(true);
  };
  const closeListMenu = () => {
    setIsOpenListMenu(false);
  };

  return (
    <div className="options">
      <nav className={"leftSide"}>
        <ul>
          <CommonAction
            orderType={orderType}
            selectedCurrentView={selectedCurrentView}
            getConsignmentByRule={getConsignmentByRule}
            setSelectedCurrentView={setSelectedCurrentView}
            setConsignmentsParamsLocal={setConsignmentsParamsLocal}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            allocation={allocation}
            storeId={orderConfig?.storeId}
            pricelistId={orderConfig?.pricelistId}
          />

          <ShipmentOrderActionWithCheck
            lhs={orderType}
            rhs={"shipment"}
            selectedOrderFromListview={selectedOrderFromListview}
            setSelectedOrder={setSelectedOrder}
            getConsignmentByRule={getConsignmentByRule}
          />

          <PurchaseOrderListActionsWithCheck
            lhs={orderType}
            rhs={"purchase"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
            getConsignmentByRule={getConsignmentByRule}
          />

          <TransferOrderActionWithCheck
            lhs={orderType}
            rhs={"transfer"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />

          <InventoryDetailActionWithCheck
            lhs={orderType}
            rhs={"inventoryDetail"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />

          <BillActionWithCheck
            lhs={orderType}
            rhs={"bill"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <PrepaymentActionWithCheck
            lhs={orderType}
            rhs={"prePayment"}
            selectedOrderFromListview={selectedOrderFromListview}
            setSelectedOrder={setSelectedOrder}
            getConsignmentByRule={getConsignmentByRule}
          />

          <SaleInvoiceActionWithCheck
            lhs={orderType}
            rhs={"saleInvoice"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <ChartsAccountActionWithCheck
            lhs={orderType}
            rhs={"chartsOfAccount"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />

          <ProductPlanActionWithCheck
            lhs={orderType}
            rhs={"productPlan"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />

          <TaxCategoryActionWithCheck
            lhs={orderType}
            rhs={"taxCategory"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />

          <ReplenishmentActionWithCheck
            lhs={orderType}
            rhs={"stockReplenishment"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <PlannedOrderActionWithCheck
            lhs={orderType}
            rhs={"plannedOrder"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <CustomerSegmentActionWithCheck
            lhs={orderType}
            rhs={"customerSegment"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <PriceListActionWithCheck
            lhs={orderType}
            rhs={"pricelist"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <CatalogueActionWithCheck
            lhs={orderType}
            rhs={"shopifyCatalogue"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
            storeId={orderConfig?.storeId}
          />

          <PromoActionWithCheck
            lhs={orderType}
            rhs={"promo"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />

          <BomActionWithCheck
            lhs={orderType}
            rhs={"boms"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <ManufacturingOrderActionWithCheck
            lhs={orderType}
            rhs={"manufacturingOrder"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
          />
          <CoManufacturingOrderActionWithCheck
            lhs={orderType}
            rhs={"coManufacturingOrder"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <QuotationActionWithCheck
            lhs={orderType}
            rhs={"quote"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <SalesOrderActionWithCheck
            lhs={orderType}
            rhs={"salesOrder"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />
          <PicklistActionWithCheck
            lhs={orderType}
            rhs={"picklist"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <StockPackActionWithCheck
            lhs={orderType}
            rhs={"stockPack"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <DeliveryActionWithCheck
            lhs={orderType}
            rhs={"delivery"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <ShipmentV1ActionWithCheck
            lhs={orderType}
            rhs={"shipmentV1"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          <AsnActionWithCheck
            lhs={orderType}
            rhs={"asn"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
            callbacks={callbacks}
          />

          <ShipmentLegActionWithCheck
            lhs={orderType}
            rhs={"shipmentLeg"}
            selectedOrderId={selectedOrderId}
            setSelectedOrder={setSelectedOrder}
            selectedOrderFromListview={selectedOrderFromListview}
          />

          {allocation && (
            <AllocationAction
              selectedOrderId={selectedOrderId}
              selectedOrderFromListview={selectedOrderFromListview}
              setSelectedOrder={setSelectedOrder}
              allocation={allocation}
            />
          )}

          {orderType === "receipt" && (
            <ReceiptActions
              selectedOrderFromListview={selectedOrderFromListview}
              setSelectedOrder={setSelectedOrder}
            />
          )}

          <CommonActionForMaster orderType={orderType} selectedOrderId={selectedOrderId} />

          {orderType === "users" && (
            <>
              <li>
                <button
                  type="button"
                  className="tableButton activeState"
                  onClick={() => onInviteClick?.()}
                >
                  Invite
                </button>
              </li>
              <UsersListActions
                selectedOrderFromListview={selectedOrderFromListview}
                setSelectedOrder={setSelectedOrder}
                onChanged={getConsignmentByRule}
              />
            </>
          )}
        </ul>
      </nav>

      <nav className="rightSide">
        <div className="selectOptions">
          {setAllocation && (
            <>
              {isOpenAllocation && (
                <SelectAllocation
                  closeModal={closeAllocation}
                  setAllocation={setAllocation}
                  selectedOption={allocation}
                />
              )}
              <div
                className={isOpenAllocation ? "milestoneBtn activeMilestone" : "milestoneBtn"}
                onClick={openAllocation}
              >
                {allocationDataByType[allocation]?.title}
                {isOpenAllocation ? <UpIcon /> : <Down />}
              </div>
            </>
          )}
          {(orderType === "shipment" || orderType === "purchase") && (
            <>
              <BoardSearch listParams={listParams} setListParams={setConsignmentsParamsLocal} />
              {orderType === "shipment" && onTransportModeChange && (
                <ShipmentTransportModeFilter
                  value={transportMode}
                  onChange={onTransportModeChange}
                />
              )}
              {(orderType === "shipment" || orderType === "purchase") &&
                boardView &&
                onHideEmptyLanesModeChange && (
                  <HideEmptyLanesMenu
                    mode={hideEmptyLanesMode}
                    onChange={onHideEmptyLanesModeChange}
                    selectedCurrentView={selectedCurrentView}
                  />
                )}
              <Milestone
                isOpenMilestone={isOpenMilestone}
                closeMilestoneModal={closeMilestoneModal}
                orderType={orderType}
              />
              <div
                className={isOpenMilestone ? "milestoneBtn activeMilestone" : "milestoneBtn"}
                onClick={openMileStoneView}
              >
                Milestones
              </div>
            </>
          )}

          {orderType === "shopifyCatalogue" && (
            <CatalogueRightAction storeId={orderConfig?.storeId} />
          )}

          {orderType === "shopifyLinkedProducts" && (
            <>
              <BoardSearch listParams={listParams} setListParams={setConsignmentsParamsLocal} />
              <ShopifyLinkedProductsRightAction
                storeId={orderConfig?.storeId}
                isSyncRunning={orderConfig?.isSyncRunning}
                onSyncStarted={orderConfig?.onSyncStarted}
              />
            </>
          )}

          {orderType === "plannedOrder" && setConsignmentsParamsLocal && (
            <label
              className={`milestoneBtn includeConvertedToggle${
                listParams?.include_converted ? " activeMilestone" : ""
              }`}
            >
              <input
                type="checkbox"
                checked={Boolean(listParams?.include_converted)}
                onChange={event => {
                  setConsignmentsParamsLocal(
                    applyIncludeConvertedParams(listParams, event.target.checked),
                  );
                }}
                onClick={event => event.stopPropagation()}
              />
              Include Converted
            </label>
          )}

          {!hideViews && (
            <span data-guide={`list.${orderType}.views`} style={{ display: "contents" }}>
              <SelectComponent
                boardView={boardView}
                setIsOpenCreateView={setIsOpenCreateView}
                selectedCurrentView={selectedCurrentView}
                setSelectedCurrentView={setSelectedCurrentView}
                title={selectedCurrentView?.value || "Default View"}
                LeftIcon={Eye}
                orderType={orderType}
                setBoardView={setBoardView}
                allocation={allocation}
              />
            </span>
          )}
        </div>
        {(orderType === "shipment" || orderType === "purchase") && (
          <div className="viewIcon">
            <button
              className={boardView ? "listIcon" : "listIcon activeIcon"}
              onClick={handleListView}
            >
              <ListIcon color={boardView ? "#8C8CA2" : `var(--DARK-BLUE, #1249BF)`} />
            </button>
            <button
              className={boardView ? "boardIcon activeIcon" : "boardIcon"}
              onClick={handleBoardView}
            >
              <BoardIcon color={boardView ? `var(--DARK-BLUE, #1249BF)` : "#8C8CA2"} />
            </button>
          </div>
        )}

        {["purchase", "transfer"].includes(allocation) && isOpenListMenu && (
          <ListMenuOptions
            closeModalMenuModal={closeListMenu}
            allocation={allocation}
            selectedOrderIds={selectedOrderId}
            selectedOrderFromListview={selectedOrderFromListview}
            setSelectedOrder={setSelectedOrder}
          />
        )}
        {["purchase", "transfer"].includes(allocation) && (
          <span className="menuIcon" onClick={openListMenu}>
            <MenuIcon />
          </span>
        )}
        <div id="options-right-burger-slot" className="optionsBurgerSlot" />
      </nav>
    </div>
  );
}

/**
 * Bulk Approve / Cancel actions for *Receipt* selected in a list view.
 *
 * Props
 * -----
 * selectedOrderFromListview: Array<{
 *   id: (number|string),
 *   approval_status?: string,
 *   cancellation_status?: string,
 *   document_type?: { id?: string, name?: string },
 * }
 * setSelectedOrder: Func>
 */
const CMO_RECEIPT_DOCUMENT_TYPE = "Co-Manufacturing Receipt";

const isCoManufacturingReceipt = item => {
  const docType = item?.document_type;
  return docType?.id === CMO_RECEIPT_DOCUMENT_TYPE || docType?.name === CMO_RECEIPT_DOCUMENT_TYPE;
};

const ReceiptActions = ({ selectedOrderFromListview, setSelectedOrder }) => {
  const [updateBulkReceiptLineStatus, { isLoading }] = useUpdateBulkReceiptLineStatusMutation();

  const notify = useNotification();

  // Normalize selection
  const selected = useMemo(() => selectedOrderFromListview ?? [], [selectedOrderFromListview]);

  /**
   * Centralized per‑action rules. Memoized so stable across renders.
   */
  const ACTION_CONFIG = useMemo(
    () => ({
      approve: {
        payloadStatus: "approved",
        successMsg: "Receipt(s) approved successfully.",
        errorMsg: "Failed to approve receipt(s).",
        disabledMsg: "Select at least one receipt that is not fully approved or cancelled.",
        cmoMultiSelectMsg: "Only one co-manufacturing receipt can be approved at a time.",
        isEligible: item =>
          item?.cancellation_status !== "Fully Cancelled" &&
          item?.approval_status !== "Fully Approved",
      },
      cancel: {
        payloadStatus: "cancelled",
        successMsg: "Receipt(s) cancelled successfully.",
        errorMsg: "Failed to cancel receipt(s).",
        disabledMsg: "Select at least one receipt that is not fully cancelled.",
        isEligible: item => item?.cancellation_status !== "Fully Cancelled",
      },
    }),
    [],
  );

  // Apply eligibility rules
  const eligibleApprove = useMemo(
    () => selected.filter(ACTION_CONFIG.approve.isEligible),
    [selected, ACTION_CONFIG],
  );
  const eligibleCancel = useMemo(
    () => selected.filter(ACTION_CONFIG.cancel.isEligible),
    [selected, ACTION_CONFIG],
  );

  const selectedCmoReceiptCount = useMemo(
    () => selected.filter(isCoManufacturingReceipt).length,
    [selected],
  );

  const blocksMultiCmoApprove = selectedCmoReceiptCount > 1;

  /**
   * Disable rules:
   *  - No selection at all
   *  - Some (or all) selected rows are ineligible
   *  - More than one Co-Manufacturing receipt selected for approve
   *  - API mutation currently loading
   *
   * This enforces "all selected must be actionable".
   */
  const isApproveDisabled =
    selected.length === 0 ||
    eligibleApprove.length !== selected.length ||
    blocksMultiCmoApprove ||
    isLoading;

  const isCancelDisabled =
    selected.length === 0 || eligibleCancel.length !== selected.length || isLoading;

  const approveDisabledTitle = blocksMultiCmoApprove
    ? ACTION_CONFIG.approve.cmoMultiSelectMsg
    : ACTION_CONFIG.approve.disabledMsg;

  const extractFriendlyBulkError = (err, fallbackMsg) => {
    const data = err?.data;
    if (!data) return fallbackMsg;

    const nonField = data.non_field_errors;
    if (Array.isArray(nonField) && nonField.length > 0) {
      return nonField[0] || fallbackMsg;
    }
    if (typeof nonField === "string" && nonField) {
      return nonField;
    }

    // Code array & detail array must both exist
    const codes = Array.isArray(data.code) ? data.code : null;
    const details = Array.isArray(data.detail) ? data.detail : null;

    if (codes && details && codes.length === details.length) {
      const idx = codes.findIndex(c => c === "STK-LINE-NA-WQ");
      if (idx !== -1) {
        return details[idx] || fallbackMsg;
      }
    }
    return fallbackMsg;
  };

  // Shared submit helper
  const submit = useCallback(
    async (actionKey, eligibleItems) => {
      const cfg = ACTION_CONFIG[actionKey];
      if (!cfg) return;

      // Guard: nothing eligible (shouldn't happen if disabled properly, but defensive)
      if (eligibleItems.length === 0) {
        notify({ type: "error", message: cfg.disabledMsg });
        return;
      }

      if (actionKey === "approve" && eligibleItems.filter(isCoManufacturingReceipt).length > 1) {
        notify({ type: "error", message: cfg.cmoMultiSelectMsg });
        return;
      }

      const ids = eligibleItems.map(i => i.id);

      try {
        await updateBulkReceiptLineStatus({
          receipt_ids: ids,
          status: cfg.payloadStatus,
        }).unwrap();
        setSelectedOrder([]);
        notify({ type: "success", message: cfg.successMsg });
      } catch (err) {
        const friendlyMessage = extractFriendlyBulkError(err, cfg.errorMsg);
        notify({ type: "error", message: friendlyMessage });
      }
    },
    [ACTION_CONFIG, updateBulkReceiptLineStatus, notify, setSelectedOrder],
  );

  const handleApprove = useCallback(
    () => submit("approve", eligibleApprove),
    [submit, eligibleApprove],
  );
  const handleCancel = useCallback(
    () => submit("cancel", eligibleCancel),
    [submit, eligibleCancel],
  );

  return (
    <>
      <li>
        <button
          className={`tableButton ${!isApproveDisabled ? "activeState" : "disableState"}`}
          disabled={isApproveDisabled}
          title={isApproveDisabled ? approveDisabledTitle : undefined}
          onClick={handleApprove}
        >
          Approve
        </button>
      </li>

      <li>
        <button
          className={`tableButton ${!isCancelDisabled ? "activeState" : "disableState"}`}
          disabled={isCancelDisabled}
          title={isCancelDisabled ? ACTION_CONFIG.cancel.disabledMsg : undefined}
          onClick={handleCancel}
        >
          Cancel
        </button>
      </li>
    </>
  );
};

export { ReceiptActions };

export { OptionsForView };
